<template>
    <report-section
        :id="id"
        title="Forest Disturbances"
        class="forest-disturbances"
    >
        <div v-if="initialLoad || forestDisturbancesData.length">
            <p>
                The watershed that you selected is outlined in orange on the map below. The H60 portion
                of the watershed is highlighted in dark orange. The charts and map below are interactive.
                <span class="is-hidden-mobile">
                    The histograms to the right of the map show the relative proportion of the disturbance segregated
                    by type, year of disturbance, Biogeoclimatic Zone, and topographic aspect, elevation, and
                    slope. You may interact with the charts and explore relationships between these attributes
                    directly by selecting the data range of interest on one or more charts to show where in the
                    watershed those areas of disturbance exist.
                </span>
            </p>
            <p>
                Forest disturbance at different ranges of elevation has different effects on dominant streamflow
                generation processes<sup>
                    <footnote-tooltip
                        :footnote-number="1"
                    />
                </sup>. The literature suggests that higher elevations above the H60 line may be
                considered to be the source area for major snowmelt peak flows. In lower elevations outside of the
                H60 area, peak flows may be generated by rainfall or rain-on-snow.
            </p>
            <p>
                Areas of change at an area with high slope may be a sediment source in a stream<sup>
                    <footnote-tooltip
                        :footnote-number="1"
                    />
                </sup>. This is due to instability due to lack of vegetation that may lead to increased erosion,
                instability and landslides.
            </p>
            <p>
                These are general guidelines and may not be relevant for the watershed selected. Further, detailed
                analysis of individual watersheds considering the unique climate and other relevant physical
                characteristics, combined with professional discretion in interpretation of the data should be
                undertaken as needed.
            </p>
            <div
                class="forest-disturbances-container"
            >
                <forest-disturbances-map
                    :watershed="reportData.selectedWatershed"
                    :disturbances="filterableForestDisturbancesData"
                    :h60-areas="h60Areas"
                    @forest-disturbances-data-change="onForestDisturbancesDataChange"
                    @map-loaded-zoom="(zoom) => mapZoom = zoom"
                />
                <crossfilter-control
                    v-if="mapZoom && forestDisturbancesData"
                    class="is-hidden-mobile"
                    :crossfilter-data="forestDisturbancesData"
                    :initial-map-zoom="mapZoom"
                    @crossfilter-update="updateForestDisturbancesData"
                    @reset-filters="resetForestDisturbancesData"
                />
            </div>
        </div>
        <div v-else>
            <p>There is no forest disturbances data available.</p>
        </div>
    </report-section>
</template>

<script>
import sectionMixin from '@foundryspatial/report-section/mixin';

import CrossfilterControl from '@/components/EquivalentClearcutAreaReport/CrossfilterControl.vue';
import ForestDisturbancesMap from '@/components/EquivalentClearcutAreaReport/ForestDisturbancesMap.vue';

export default {
    name: 'ForestDisturbancesSection',

    components: {
        CrossfilterControl,
        ForestDisturbancesMap,
    },

    mixins: [
        sectionMixin,
    ],

    data: () => ({
        filterableForestDisturbancesData: [],
        forestDisturbancesData: [],
        initialLoad: true,
        mapZoom: null,
    }),

    computed: {
        h60Areas() {
            if (this.reportData.rollup && this.reportData.rollup.h60_geom) {
                return this.reportData.rollup.h60_geom;
            }
            return {};
        },
    },

    methods: {
        /**
         * Update crossfiltered forest disturbances data
         *
         * @param {Array} crossfilteredData - crossfiltered forest disturbances data
         */
        updateForestDisturbancesData(crossfilteredData) {
            this.filterableForestDisturbancesData = crossfilteredData;
        },

        /**
         * Reset the forest disturbances data to the unfiltered original set
         */
        resetForestDisturbancesData() {
            this.filterableForestDisturbancesData = this.forestDisturbancesData;
        },

        /**
         * Event handler when forest disturbances data changes on the map
         *
         * @param {Array} data - forest disturbances data emitted from the map
         */
        onForestDisturbancesDataChange(data) {
            this.initialLoad = false;

            if (!this.forestDisturbancesData.length) {
                this.forestDisturbancesData = data;
            }
            if (!this.filterableForestDisturbancesData.length) {
                this.filterableForestDisturbancesData = data;
            }
        },
    },
};
</script>

<style lang="scss">
.forest-disturbances.report-section {
    .forest-disturbances-container {
        width: 100%;
        display: grid;

        @include tablet {
            grid-template-columns: 2fr 1fr;
        }
    }
}
</style>
